/**
 * One ECharts canvas (decision 5.2), with only the pieces we use registered - the whole library is
 * several times larger than the charts we draw.
 *
 * A canvas carries no meaning to a screen reader, so every chart states what it shows in text and is
 * followed by its table view.
 */

import { BarChart, GraphChart } from "echarts/charts";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useEffect, useRef } from "react";

echarts.use([BarChart, GraphChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

export type ChartOption = Parameters<echarts.ECharts["setOption"]>[0];

export function Chart({
  option,
  height,
  description,
  className,
}: {
  option: ChartOption;
  height: number;
  /** What the chart shows, for anyone who cannot see it. */
  description: string;
  className?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!box.current) return;
    const instance = echarts.init(box.current, undefined, { renderer: "canvas" });
    chart.current = instance;
    const resize = new ResizeObserver(() => instance.resize());
    resize.observe(box.current);
    return () => {
      resize.disconnect();
      instance.dispose();
      chart.current = null;
    };
  }, []);

  // Redraw in place when the data changes: ECharts keeps what still applies and animates the rest.
  useEffect(() => {
    chart.current?.setOption(option, { notMerge: true });
  }, [option]);

  return (
    <div
      ref={box}
      role="img"
      aria-label={description}
      style={{ height }}
      className={className ?? "w-full"}
    />
  );
}
